import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";
import {
  approveExitInterview,
  getExitInterviewDetails,
} from "@/services/performanceService";
import { usePermission } from "@/contexts/RoleAccessContext";
import { useReviewsListPath } from "@/contexts/TeamScopeContext";
import { createCanHelpers } from "@/utils/rbac";
import {
  AppraisalHeader,
  EmployeeInfoCard,
  StatusBanner,
  SectionCard,
  QuestionDisplay,
} from "@/pages/admin/performance/components/shared";

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ExitInterviewForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { can } = usePermission();
  const reviewsPath = useReviewsListPath();
  const { canApprove } = createCanHelpers(can);

  const [interview, setInterview] = useState(null);
  const [template, setTemplate] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showFinalApprovalConfirm, setShowFinalApprovalConfirm] =
    useState(false);

  // Form state
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [generalComments, setGeneralComments] = useState("");
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [reasonOther, setReasonOther] = useState("");

  useEffect(() => {
    loadInterview();
  }, [id]);

  const loadInterview = async () => {
    try {
      setLoading(true);
      const response = await getExitInterviewDetails(id);
      const data = response.data;
      const submittedData = data.exit_interview;

      setInterview({
        id: data._id,
        employeeId: data.employee_id?.employee_id,
        employeeName: data.employee_name,
        status: data.status,
        canApprove: data.can_approve === true,
        approvalStep: data.approval_step || null,
      });
      setTemplate(data.template_id);
      setEmployee(data.employee_id || null);

      if (submittedData?.responses?.length) {
        setRatings(
          submittedData.responses.reduce(
            (acc, item) => ({
              ...acc,
              [item.question_id]: item.rating_label,
            }),
            {},
          ),
        );
        setComments(
          submittedData.responses.reduce(
            (acc, item) => ({
              ...acc,
              [item.question_id]: item.comment || "",
            }),
            {},
          ),
        );
      }
      setGeneralComments(submittedData?.overall_comment || "");
      setSelectedReasons(submittedData?.selected_reasons || []);
      setReasonOther(submittedData?.reason_other || "");
    } catch {
      toast.error("Failed to load exit interview");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!canApprove("performance_management", true)) return;

    setSubmitting(true);
    try {
      const response = await approveExitInterview(id, {
        comments: generalComments,
      });
      toast.success(
        response?.message || "Exit interview approved successfully",
      );
      navigate(`${reviewsPath}?tab=exit-interviews`);
    } catch (err) {
      toast.error(
        err.response?.data?.error || err.message || "Failed to approve",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sections = template?.sections || [];
  const allQuestionIds = sections.flatMap((s) =>
    (s.questions || []).map((q) => q.id),
  );
  const answeredCount = Object.keys(ratings).filter(
    (k) => allQuestionIds.includes(k) && ratings[k],
  ).length;
  const progressPct =
    allQuestionIds.length > 0
      ? Math.round((answeredCount / allQuestionIds.length) * 100)
      : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!interview) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        <p className="text-lg">Exit Interview not found.</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate(reviewsPath)}
        >
          Back to Reviews
        </Button>
      </div>
    );
  }

  const isCompleted = interview.status === "Completed";
  const isEligibleToApprove = interview.canApprove && !isCompleted;
  const isFinalAdminApproval = interview.approvalStep === "admin";
  const approveLabel =
    interview.approvalStep === "manager"
      ? "Approve & Send to Admin"
      : "Finalize Exit Interview";

  const status = interview.status;

  const appraisalShape = {
    cycleName: template?.name || "Exit Interview",
    employeeName: interview.employeeName,
    employeeId: interview.employeeId || employee?.employee_id,
    department: employee?.department,
  };

  const employeeShape = employee
    ? {
        employee_id:
          interview.employeeId || employee.employee_id || employee.id,
        designation: employee.designation,
        department: employee.department,
        manager: employee.manager,
        joinDate: formatDate(employee.date_of_joining),
        lastWorkingDate: formatDate(employee.last_working_date),
      }
    : null;

  const submitAction = isEligibleToApprove
    ? {
        label: approveLabel,
        icon: CheckCircle2,
        handler: () => {
          if (isFinalAdminApproval) {
            setShowFinalApprovalConfirm(true);
            return;
          }
          handleSubmit();
        },
        color: "bg-primary hover:bg-primary/90",
        disabled: progressPct < 100,
      }
    : null;

  const banner = isCompleted
    ? {
        title: "Exit Interview Completed",
        message: "This exit interview has been finalized.",
        icon: "check",
      }
    : isEligibleToApprove
      ? {
          title: isFinalAdminApproval
            ? "Final Admin Approval"
            : "Awaiting Manager Approval",
          message:
            interview.approvalStep === "manager"
              ? "Review the employee's responses and approve when ready."
              : "Review the employee's responses and finalize this exit interview.",
          icon: "alert",
        }
      : {
          title: `Exit Interview · ${status}`,
          message: "This exit interview is currently being processed.",
          icon: "clock",
        };

  return (
    <div className="max-w-7xl mx-auto pb-20">
      <AppraisalHeader
        appraisal={appraisalShape}
        status={status}
        submitAction={submitAction}
        isCompleted={isCompleted}
        isEmployeeView={false}
        onBack={() => navigate(-1)}
        submitting={submitting}
        STEPS={null}
        hidePdfExport
        template={template}
        employee={employeeShape}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-3">
          <div className="lg:sticky lg:top-24 space-y-4">
            <EmployeeInfoCard
              employee={employeeShape}
              appraisal={appraisalShape}
              status={status}
            />

            {isEligibleToApprove && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Approval Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <Badge
                    variant="outline"
                    className="border-primary/30 bg-primary/10 text-[10px] text-primary"
                  >
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    {interview.approvalStep === "manager"
                      ? "Manager Pending"
                      : "Admin Pending"}
                  </Badge>
                  <p className="mt-2 text-sm font-medium text-foreground">
                    {interview.approvalStep === "manager"
                      ? "Waiting for manager approval."
                      : "Waiting for admin approval."}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    Review the employee's responses and approve when ready.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <div className="lg:col-span-9 space-y-8">
          <StatusBanner
            title={banner.title}
            message={banner.message}
            icon={banner.icon}
          />

          {isEligibleToApprove && (
            <Card data-testid="exit-progress-banner">
              <CardContent className="py-4 sm:py-6 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-sm sm:text-base font-semibold text-foreground">
                      Interview Progress
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {answeredCount} of {allQuestionIds.length} questions
                      answered
                    </p>
                  </div>
                  <span className="text-2xl font-bold text-primary shrink-0">
                    {progressPct}%
                  </span>
                </div>
                <Progress value={progressPct} className="h-2 w-full" />
              </CardContent>
            </Card>
          )}

          {selectedReasons.length > 0 && (
            <Card data-testid="exit-reasons-completed">
              <CardHeader>
                <CardTitle className="text-base">
                  Reasons for Leaving
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {selectedReasons.map((reason) => (
                  <Badge
                    key={reason}
                    className="rounded-full border-primary/20 bg-primary/10 px-3 py-1 text-primary hover:bg-primary/10"
                  >
                    {reason}
                  </Badge>
                ))}
                {reasonOther && (
                  <p className="text-sm text-muted-foreground mt-2 w-full">
                    Other: {reasonOther}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {sections.map((section, sectionIndex) => (
            <div
              key={section.id || sectionIndex}
              data-testid={`exit-section-${section.id}`}
            >
              <SectionCard
                title={section.title}
                description={section.description}
                sectionIndex={sectionIndex}
                variant="readonly"
              >
                {(section.questions || []).map((question, qi) => (
                  <div
                    key={question.id || qi}
                    data-testid={`exit-question-${question.id}`}
                  >
                    <QuestionDisplay
                      question={question}
                      questionIndex={qi}
                      employeeResponse={
                        ratings[question.id]
                          ? {
                              rating_label: ratings[question.id],
                              rating: 0,
                              comment: comments[question.id] || "",
                            }
                          : null
                      }
                      managerResponse={null}
                      viewMode="manager"
                    />
                  </div>
                ))}
              </SectionCard>
            </div>
          ))}

          {generalComments && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Additional Comments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p
                  className="text-sm text-foreground leading-6 whitespace-pre-wrap"
                  data-testid="exit-general-comments"
                >
                  {generalComments}
                </p>
              </CardContent>
            </Card>
          )}

          {sections.length === 0 && (
            <Card className="border-amber-200 dark:border-amber-800">
              <CardContent className="py-6 text-center text-muted-foreground space-y-3">
                <p>
                  No template found for this exit interview. Please create an
                  Exit Interview template in the Appraisal Builder first.
                </p>
                <Button
                  variant="outline"
                  onClick={() => navigate("/settings/appraisal-builder")}
                >
                  Go to Builder
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={showFinalApprovalConfirm}
        onOpenChange={setShowFinalApprovalConfirm}
        title="Confirm Final Approval"
        description="This will complete the exit interview approval process for this employee."
        message={`Are you sure you want to approve ${interview.employeeName}'s exit interview?`}
        confirmText={submitting ? "Approving..." : "Confirm Approval"}
        cancelText="Cancel"
        onConfirm={handleSubmit}
      />
    </div>
  );
};

export default ExitInterviewForm;
